import React, { useRef, useEffect } from 'react'

import Button from 'app/app/(client)/components/atoms/Button/Button'
import Icon from 'app/app/(client)/components/atoms/Icon/Icon'
import { DropdownOption } from 'app/types/types'

interface DropdownProps {
  iconName?: string
  id: string
  options: DropdownOption[]
  selectedKey?: string[]
  onChange: (selectedOptionKey: string[]) => void
  className?: string
  multiselect?: boolean
  renderOption?: (option: DropdownOption, isSelected: boolean, index: number) => React.ReactNode
  placeholder?: string
  isDisable?: boolean
  dropDownOptionSize?: 'small' | 'medium' | 'large'
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  options,
  selectedKey,
  onChange,
  className = '',
  multiselect = false,
  renderOption,
  placeholder = 'Select',
  iconName,
  isDisable = false,
  dropDownOptionSize = 'medium'
}) => {
  const [open, setOpen] = React.useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Handle outside click
  useEffect(() => {
    if (!open) {
      return
    }
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Multiselect state helpers
  const isSelected = (option: DropdownOption): boolean => {
    if (!Array.isArray(selectedKey)) {
      return false
    }

    if (multiselect) {
      return selectedKey.includes(option.key)
    } else if (selectedKey.length === 1) {
      return selectedKey[0] === option.key
    }

    return false
  }

  const handleSelect = (option: DropdownOption) => {
    if (multiselect) {
      let newSelected: string[] = Array.isArray(selectedKey) ? [...selectedKey] : []

      if (newSelected.includes(option.key)) {
        newSelected = newSelected.filter(v => v !== option.key)
      } else {
        newSelected.push(option.key)
      }
      onChange(newSelected)
    } else {
      onChange([option.key])
      // To do: need to check in upcoming page designs
      // setOpen(false)
    }
  }

  // Button label
  const buttonLabel: React.ReactNode = placeholder
  // To do: need to check in upcoming page designs
  // if (multiselect) {
  //   if (Array.isArray(selectedKey) && selectedKey.length > 0) {
  //     buttonLabel = `${selectedKey.length} selected`
  //   }
  // } else {
  //     const selected = options.find(opt => opt.key === selectedKey)
  //     buttonLabel = selected ? selected.value : placeholder
  // }

  return (
    <div ref={ref} className="relative" tabIndex={0}>
      <Button
        id={id}
        className={`btn ${className}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Dropdown Button"
        type="button"
        variant="tertiary"
        size="medium"
        disabled={isDisable}
      >
        {iconName && <Icon name={iconName} className="flex h-9 py-2 justify-center items-center w-5" />}
        <span className="text-[16px] font-semibold leading-6 px-[6px] text-center">{buttonLabel}</span>
        {/* To do: need to check in upcoming page designs */}
        {/* <Icon name="icon-chevron-down" className={` text-primary text-[18px] font-semibold text-center`} /> */}
      </Button>
      {open && (
        <ul
          className={`dropDown__options dropDown__options--${dropDownOptionSize} overflow-y-auto scrollbar--custom shadow-small absolute z-10 mt-1 flex flex-col items-start p-2 bg-white border-[0.5px] border-[#D7D8DB] overflow-auto rounded-[16px] max-h-[60vh]`}
          role="listbox"
        >
          {options.map((option, index) => {
            if (typeof option.disabled === 'boolean' && option.disabled) {
              return (
                <li key={option.key} className="w-full text-grey-icon">
                  {renderOption ? renderOption(option, false, index) : option.value}
                </li>
              )
            }
            return (
              <li
                key={option.key}
                className={`cursor-pointer hover:bg-background-dropdown-hover w-full flex items-center`}
                onMouseDown={e => {
                  e.preventDefault()
                  handleSelect(option)
                }}
                role="option"
                aria-selected={isSelected(option)}
              >
                {multiselect ? (
                  <input
                    type="checkbox"
                    checked={isSelected(option)}
                    readOnly
                    className="w-[16px] h-[16px] ml-6 mr-3 accent-primary flex-shrink-0"
                    tabIndex={-1}
                  />
                ) : isSelected(option) ? (
                  <Icon name="icon-check" className="text-primary text-[24px] mr-2" />
                ) : (
                  <div className="mr-2 min-w-[24px]" />
                )}
                {renderOption ? renderOption(option, isSelected(option), index) : option.value}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default Dropdown
